import { Link, useParams } from "react-router-dom";
import { Seo, breadcrumbLd } from "../lib/seo";
import { Reveal } from "../components/Reveal";
import { Eyebrow, ArrowLink } from "../components/atoms";
import { DirectorsNote } from "../components/DirectorsNote";
import { ARTICLES, IMAGES, MATTHEW, CORPORATE } from "../data/content";

export default function InsightArticle() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);

  if (!article) {
    return (
      <section className="bg-white">
        <div className="max-w-3xl mx-auto px-6 py-32">
          <h1 className="font-serif text-4xl font-medium text-charcoal">Article not found.</h1>
          <div className="mt-8">
            <ArrowLink to="/insights" data-testid="article-not-found-back">Back to Insights</ArrowLink>
          </div>
        </div>
      </section>
    );
  }

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    datePublished: article.isoDate,
    author: {
      "@type": "Person",
      name: MATTHEW.name,
      jobTitle: MATTHEW.role,
    },
    publisher: { "@type": "Organization", name: "80 Metrics Property Advisory" },
    articleSection: article.category,
  };

  const others = ARTICLES.filter((a) => a.slug !== slug).slice(0, 2);

  return (
    <>
      <Seo
        title={`${article.title} | 80 Metrics Insights`}
        description={article.excerpt}
        jsonLd={[
          articleLd,
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Insights", path: "/insights" },
            { name: article.title, path: `/insights/${slug}` },
          ]),
        ]}
      />
      <article data-testid="article-page">
        <header className="bg-charcoal text-ivory">
          <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-14">
            <Reveal>
              <p className="text-xs text-ivory/50">
                <Link to="/insights" className="hover:text-gold transition-colors duration-300" data-testid="article-breadcrumb-insights">Insights</Link>
                <span className="mx-2">/</span>
                <span className="text-gold">{article.category}</span>
              </p>
              <h1 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-6xl font-medium leading-[1.08] tracking-tight">
                {article.title}
              </h1>
              <p className="mt-6 text-xs tracking-wide text-ivory/50">
                {article.date} · {article.read} · By{" "}
                <Link to="/about" className="text-gold hover:text-ivory transition-colors duration-300">{MATTHEW.name}</Link>, {MATTHEW.role}
              </p>
            </Reveal>
          </div>
        </header>

        <div className="bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-10 -mt-0 py-10">
            <Reveal>
              <div className="aspect-[16/8] overflow-hidden bg-bone">
                <img src={IMAGES[article.image].src} alt={IMAGES[article.image].alt} className="h-full w-full object-cover grayscale-[25%]" data-testid="article-image" />
              </div>
            </Reveal>
          </div>
          <div className="max-w-3xl mx-auto px-6 lg:px-10 pb-20">
            {article.body.map((p, i) => (
              <Reveal key={i} delay={0.03 * i}>
                <p className={`text-charcoal/80 leading-[1.9] ${i === 0 ? "font-serif text-xl lg:text-2xl text-charcoal" : "text-base mt-7"}`}>
                  {p}
                </p>
              </Reveal>
            ))}
            <Reveal className="mt-14 border-t border-charcoal/15 pt-8">
              <p className="text-[11px] font-semibold tracking-[0.28em] uppercase text-golddark">Written by</p>
              <p className="mt-3 font-serif text-2xl font-medium text-charcoal">
                <Link to="/about" className="hover:text-golddark transition-colors duration-300">{MATTHEW.name}</Link>
              </p>
              <p className="mt-1 text-sm text-charcoal/60">{MATTHEW.role}</p>
              <p className="mt-3 text-xs text-charcoal/50 leading-relaxed">{MATTHEW.quals} · {CORPORATE.entity} · {CORPORATE.licence}</p>
            </Reveal>
          </div>
        </div>
      </article>

      <section className="bg-bone">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <DirectorsNote dark={false} />
          <div>
            <Eyebrow>Keep reading</Eyebrow>
            <div className="mt-6 space-y-8">
              {others.map((a) => (
                <Link key={a.slug} to={`/insights/${a.slug}`} className="group block border-b border-charcoal/15 pb-8" data-testid={`related-${a.slug}`}>
                  <p className="text-[11px] font-semibold tracking-[0.24em] uppercase text-golddark">{a.category}</p>
                  <h3 className="mt-2 font-serif text-2xl font-medium text-charcoal group-hover:text-golddark transition-colors duration-300">
                    {a.title}
                  </h3>
                  <p className="mt-2 text-xs text-charcoal/45">{a.date} · {a.read}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
